import { useEffect, useState } from "react";
import { DataTable, DataTableStateEvent, SortOrder } from "primereact/datatable";
import { Column } from "primereact/column";
import { CheckCircleIcon, NoSymbolIcon, UsersIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../../../app/hooks";
import Select from "../../../../components/input/Select";
import SearchFilter from "../../../../components/input/SearchFilter";
import { Loading } from "../../../../components/Loading";
import SimplePagination from "../../../../components/SimplePagination";
import { CustomSortIcon } from "../../../../components/DatatableHelpers";
import { ClassLinkOrCleverIcon } from "../../../../components/ClassLinkOrCleverIcon";
import { SchoolFilter } from "../../../schools/schoolsApiSlice";
import {
	DistrictSchoolListQueryModel,
	DistrictSchoolListViewModel,
	useSchoolListByDistrictQuery,
} from "../../districtsApiSlice";

interface DistrictSchoolsProps {
	districtId: number;
}

export default function DistrictSchoolsSection(props: DistrictSchoolsProps) {
	const initQueryargs: DistrictSchoolListQueryModel = {
		districtId: props.districtId,
		active: "Active",
	};

	const { config } = useAppSelector((state) => state.site);
	const [queryargs, setQueryargs] = useState(initQueryargs);
	const [page, setPage] = useState(0);
	const [sortField, setSortField] = useState("name");
	const [sortOrder, setSortOrder] = useState<SortOrder>(1);
	const [searchFilter, setSearchFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState(initQueryargs.active);
	const [isFiltered, setIsFiltered] = useState(false);

	const schoolListResult = useSchoolListByDistrictQuery(queryargs);
	const data = schoolListResult.data;

	// Filter schools locally based on search filter
	const filteredSchools =
		data?.schools.filter((school) => {
			if (!searchFilter) return true;
			const searchLower = searchFilter.toLowerCase();
			return school.name?.toLowerCase().includes(searchLower);
		}) ?? [];

	const sortedSchools = [...filteredSchools].sort((a, b) => {
		if (!sortField || sortOrder === 0 || sortOrder == null) return 0;

		// Special handling for seats sorting
		if (sortField === "seats") {
			// Sort by seatsInUse first, then by seatsAllocated as tiebreaker
			if (a.seatsInUse !== b.seatsInUse) {
				return (a.seatsInUse - b.seatsInUse) * sortOrder;
			}
			return (a.seatsAllocated - b.seatsAllocated) * sortOrder;
		}

		const aValue = a[sortField as keyof DistrictSchoolListViewModel];
		const bValue = b[sortField as keyof DistrictSchoolListViewModel];

		// Handle null/undefined values
		if (aValue == null && bValue == null) return 0;
		if (aValue == null) return sortOrder;
		if (bValue == null) return -sortOrder;

		// Compare values
		if (aValue < bValue) return -sortOrder;
		if (aValue > bValue) return sortOrder;
		return 0;
	});

	const handleSort = (event: DataTableStateEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder ?? 0);
		setPage(0);
	};

	useEffect(() => {
		setQueryargs({
			...queryargs,
			districtId: props.districtId,
			active: statusFilter,
		});
	}, [page, statusFilter]);

	useEffect(() => {
		setIsFiltered(!!searchFilter);
		setPage(0);
	}, [searchFilter, statusFilter]);

	const hasClassLinkOrCleverSchool = data?.schools.some(
		(s) => s.isClassLinkSchool || s.isCleverSchool
	);

	const activeBodyTemplate = (row: DistrictSchoolListViewModel) => {
		return (
			<span className="flex justify-center">
				{row.active ? (
					<CheckCircleIcon
						className="size-6 text-green-500"
						data-tooltip-id={`enabled-${row.id}-tooltip`}
					/>
				) : (
					<NoSymbolIcon
						className="size-6 text-red-500"
						data-tooltip-id={`enabled-${row.id}-tooltip`}
					/>
				)}
			</span>
		);
	};

	const cleverOrClassLinkBodyTemplate = (row: DistrictSchoolListViewModel) => {
		return (
			<ClassLinkOrCleverIcon
				isClassLinkEntity={!!row.isClassLinkSchool}
				isCleverEntity={!!row.isCleverSchool}
			/>
		);
	};

	const contactBodyTemplate = (row: DistrictSchoolListViewModel) => {
		return (
			<span>
				<span>{row.contact}</span>
				<div className="mt-1 text-gray-500 text-sm">{row.phone}</div>
			</span>
		);
	};

	const seatsBodyTemplate = (row: DistrictSchoolListViewModel) => {
		return (
			<span className="tracking-widest">
				<span className="text-lg font-medium">{row.seatsInUse}</span>/
				<span className="text-sm">{row.seatsAllocated}</span>
			</span>
		);
	};

	// Pagination
	const pageSize = 10;
	const paginatedSchools = sortedSchools.slice(page * pageSize, (page + 1) * pageSize);
	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	return (
		<section>
			<hr className="my-6" />
			<h2>Schools</h2>
			<div className="flex justify-between items-end">
				<div className="flex gap-3 mt-4 items-end">
					{!config?.isUtahComposeSite && (
						<Select
							value={statusFilter}
							setValue={(v) => setStatusFilter(v as string)}
							label="Status"
							simpleListBoxId="statusFilter"
							options={[
								{
									value: "Active",
									label: SchoolFilter.Active,
								},
								{
									value: "Inactive",
									label: SchoolFilter.Inactive,
								},
								{
									value: "All",
									label: SchoolFilter.All,
								},
							]}
							fieldClasses="w-56"
						/>
					)}
					<SearchFilter
						id="courseSearch"
						name="courseSearch"
						searchFilter={searchFilter}
						setSearchFilter={setSearchFilter}
						label="Search"
						placeHolder="Search by name..."
						className="w-80"
					/>
				</div>
				{!config?.isUtahComposeSite && (
					<div className="flex items-end">
						<UsersIcon className="size-12" />
						<div className="ml-4">
							<p>Seat Count</p>
							<div className="flex items-end">
								<span className="tracking-widest text-xl ml-2 mr-4">
									<span className="text-xl font-medium">
										{data?.totalSeatsInUse}
									</span>
									/<span className="text-lg">{data?.totalSeatsAllocated}</span>
								</span>
							</div>
						</div>
					</div>
				)}
			</div>
			<Loading loading={schoolListResult.isLoading} />
			{data && (
				<DataTable
					value={paginatedSchools}
					dataKey="id"
					emptyMessage="No schools found"
					stripedRows
					sortField={sortField}
					sortOrder={sortOrder}
					onSort={handleSort}
					sortIcon={CustomSortIcon}
					className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
					style={{ marginTop: "16px" }}>
					<Column
						header="Active"
						body={activeBodyTemplate}
						headerClassName="!w-12"
						className="!w-12 !text-center"
						sortable
						sortField="active"
					/>
					{!config?.isUtahComposeSite && hasClassLinkOrCleverSchool && (
						<Column
							body={cleverOrClassLinkBodyTemplate}
							headerClassName="!px-0 w-4"
							className="!px-0"
						/>
					)}
					<Column header="ID" field="externalID" sortable sortField="externalID" />
					<Column header="School Name" field="name" sortable sortField="name" />
					<Column
						header="Primary Contact"
						body={contactBodyTemplate}
						sortable
						sortField="contact"
					/>
					<Column
						header="# of Courses"
						field="numCourses"
						sortable
						sortField="numCourses"
					/>
					{!config?.isUtahComposeSite && (
						<Column
							header="# of Seats"
							body={seatsBodyTemplate}
							sortable
							sortField="seats"
						/>
					)}
				</DataTable>
			)}
			<SimplePagination
				itemCount={sortedSchools.length}
				page={page}
				pageSize={pageSize}
				totalCount={data?.schools.length}
				isFiltered={isFiltered}
				onChange={(page) => handlePageChange(page)}
				className={"border-none"}
			/>
		</section>
	);
}
